import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Public props for `SidekickErrorBoundary`. Re-exported from the
 * package barrel so consumers can type their own wrappers without
 * reaching into the component file.
 */
export type SidekickErrorBoundaryProps = {
  /**
   * The subtree the boundary guards. Render failures inside `children`
   * cause the boundary to render `null` instead of letting the error
   * propagate to the host.
   */
  children: ReactNode;
  /**
   * Fires when the boundary catches a render error in its subtree.
   * Hosts wire this to their telemetry SDK (e.g.,
   * `datadogRum.addError`) so failures show up in incident triage.
   *
   * Implementations MUST NOT throw — the boundary calls this from
   * `componentDidCatch` and a thrown handler surfaces as a secondary
   * problem inside React's own error pipeline. Wrap risky work in a
   * try/catch on the host side.
   *
   * When omitted, the boundary falls back to `console.error` to match
   * the repo's existing error-boundary pattern (see
   * `apps/mf-insights/src/components/common/error/ErrorBoundary.tsx`)
   * so a missing wiring doesn't silently swallow errors.
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type SidekickErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Silent error boundary scoped to the Sidekick panel slot.
 *
 * Renders `null` on render failure so the rest of the host route keeps
 * working. The Sidekick is additive UI; a visible error frame in its
 * place would be more alarming than its absence, so this boundary
 * deliberately does not show a retry surface. Consumers that lazy-load
 * the panel via `React.lazy(...)` get chunk-load failures handled by
 * this boundary too — the rejected import reaches `componentDidCatch`
 * and is forwarded to `onError`.
 */
class SidekickErrorBoundary extends Component<
  SidekickErrorBoundaryProps,
  SidekickErrorBoundaryState
> {
  constructor(props: SidekickErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SidekickErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
      return;
    }
    console.error(
      '[AI Sidekick] Failed to mount panel; hiding panel content.',
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export default SidekickErrorBoundary;
